// ------------------------------------------------------------
// First Created: 07 Jan 2026
// Author: Atul Pandey
// Purpose:
// Show collective action performance over time (frontend-only)
//
// What this chart shows:
// - One line per contributor (user-centric) showing actualValue snapshots
// - One cumulative collective line computed using deltas (no double counting)
// - Chronological master timeline across all collective action logs
//
// Notes:
// - Collective is NOT sum(actualValue). It is sum(delta) across events.
// - Delta is computed per user as max(0, current - prev) for OneTime Outcomes.
// - Each user line forward-fills last known actualValue to keep continuity.
// - Filters (Habits-style) to toggle users on/off.
// ------------------------------------------------------------

import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import moment from "moment";


// ------------------------------------------------------------
// Color palette (stable, restrained)
// ------------------------------------------------------------
const USER_COLORS = [
  "#6366f1", // indigo
  "#16a34a", // green
  "#0284c7", // sky
  "#7c3aed", // violet
  "#ea580c", // orange
  "#0f766e", // teal
  "#9333ea", // purple
  "#ca8a04", // amber
];


// ------------------------------------------------------------
// Step 1 - Extract all user events from collectiveActions
// Each event is an "actualValue snapshot" at a timestamp.
// ------------------------------------------------------------
const extractCollectiveEvents = (collectiveActions = []) => {
  const events = [];

  collectiveActions.forEach((action) => {
    const user = action?.userId;

    const firstName = (user.name || user.username || "User").split(" ")[0];
    if (!user?._id) return;

    const notes = (action.userActionNotes || [])
      .filter((n) => n?.actionNoteDate && n?.actionNoteActualValue !== undefined && n?.actionNoteActualValue !== null)
      .sort((a, b) => moment(a.actionNoteDate).diff(moment(b.actionNoteDate)));

    notes.forEach((note) => {
      events.push({
        dateKey: moment(note.actionNoteDate).toISOString(), // keep timestamp precision
        userId: user._id,
        // userName: user.name || user.username || "User",
        userName: firstName,
        actualValue: Number(note.actionNoteActualValue) || 0,
      });
    });
  });

  // chronological ordering across all users
  events.sort((a, b) => moment(a.dateKey).diff(moment(b.dateKey)));

  return events;
};


// ------------------------------------------------------------
// Step 2 - Build master timeline (x-axis points)
// We use xValue as numeric index so multiple timestamps on same day still show.
// ------------------------------------------------------------
const buildTimelinePoints = (events = []) => {
  const unique = [];
  const seen = new Set();

  events.forEach((e) => {
    if (seen.has(e.dateKey)) return;
    seen.add(e.dateKey);
    unique.push(e.dateKey);
  });

  // unique is already chronological because events are sorted
  return unique.map((dateKey, idx) => ({
    dateKey,
    xValue: idx,
    xLabel: moment(dateKey).format("DD MMM"),
    xLabelLong: moment(dateKey).format("DD MMM, h:mm A"),
  }));
};


// ------------------------------------------------------------
// Step 3 - Build per-user forward-filled actualValue series
// Each user gets a value at every timeline point (null until first log, then forward-fill).
// ------------------------------------------------------------
const buildPerUserSeries = (timeline, collectiveActions, events) => {
  const users = [
    ...new Map(
      (collectiveActions || [])
        .filter((a) => a?.userId?._id)
        .map((a) => [a.userId._id, a.userId])
    ).values(),
  ];

  const userSeriesMap = {};
  const eventsByUser = {};

  users.forEach((u) => {
    eventsByUser[u._id] = [];
  });

  events.forEach((e) => {
    if (!eventsByUser[e.userId]) eventsByUser[e.userId] = [];
    eventsByUser[e.userId].push(e);
  });

  users.forEach((user) => {
    let lastValue = null;

    const firstName = (user.name || user.username || "User").split(" ")[0];

    userSeriesMap[user._id] = {
      userId: user._id,
    //   userName: user.name || user.username || "User",
      userName: firstName,
      points: timeline.map((t) => {
        const match = (eventsByUser[user._id] || []).find((ev) => ev.dateKey === t.dateKey);

        if (match) {
          lastValue = match.actualValue;
          return { xValue: t.xValue, value: match.actualValue };
        }

        // forward fill after first known value, else null
        return { xValue: t.xValue, value: lastValue };
      }),
    };
  });

  return userSeriesMap;
};


// ------------------------------------------------------------
// Step 4 - Build collective cumulative series using deltas
// Delta per event = max(0, currentActual - prevActual for that user).
//
// Why max(0, ...)?
// - OneTime actualValue should not decrease.
// - If user edited notes and intermediate values exist, we should avoid negative deltas.
// ------------------------------------------------------------
const buildCollectiveCumulativeSeries = (timeline, events) => {
  const prevByUser = {}; // userId -> last actualValue seen
  const cumulativeByDateKey = {}; // dateKey -> cumulative value at that point

  let running = 0;

  timeline.forEach((t) => {
    // process all events at this exact timestamp
    const thisEvents = events.filter((e) => e.dateKey === t.dateKey);

    thisEvents.forEach((e) => {
      const prev = prevByUser[e.userId] ?? 0;
      const deltaRaw = e.actualValue - prev;
      const delta = Math.max(0, deltaRaw);

      running += delta;
      prevByUser[e.userId] = Math.max(prev, e.actualValue); // keep monotonic
    });

    cumulativeByDateKey[t.dateKey] = running;
  });

  return timeline.map((t) => ({
    xValue: t.xValue,
    value: cumulativeByDateKey[t.dateKey] ?? 0,
  }));
};


// ------------------------------------------------------------
// Main Component
// ------------------------------------------------------------
const EtUserActionPerformanceCollectiveTimeSeriesChart = ({
  rootAction,
  collectiveActions = [],
  loggedInUserId,
}) => {

  // ------------------------------------------------------------
  // Step 0 - Build all derived series first (NO guards before hooks)
  // ------------------------------------------------------------
  const events = useMemo(() => {
    return extractCollectiveEvents(collectiveActions);
  }, [collectiveActions]);

  const timeline = useMemo(() => {
    return buildTimelinePoints(events);
  }, [events]);

  const userSeriesMap = useMemo(() => {
    return buildPerUserSeries(timeline, collectiveActions, events);
  }, [timeline, collectiveActions, events]);

  const collectiveSeries = useMemo(() => {
    return buildCollectiveCumulativeSeries(timeline, events);
  }, [timeline, events]);

  // ------------------------------------------------------------
  // Step 1 - Habits-style visibility chips
  // ------------------------------------------------------------
  const [visibleUsers, setVisibleUsers] = useState(() => Object.keys(userSeriesMap || {}));

  // Keep visibleUsers in sync if user set changes (rare but possible)
  useMemo(() => {
    const ids = Object.keys(userSeriesMap || {});
    setVisibleUsers((prev) => {
      // keep only those still present, plus any new ones default-visible
      const kept = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !kept.includes(id));
      return [...kept, ...added];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(userSeriesMap || {}).join("|")]);

  const toggleUser = (userId) => {
    setVisibleUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // ------------------------------------------------------------
  // Step 2 - Flatten into chartData rows (one row per timeline point)
  // - Each user column is their forward-filled actualValue snapshot
  // - Collective column is cumulative delta-based total
  // ------------------------------------------------------------
  const chartData = useMemo(() => {
    if (!timeline.length) return [];

    return timeline.map((t) => {
      const row = {
        xValue: t.xValue,
        xLabel: t.xLabel,
        xLabelLong: t.xLabelLong,
      };

      Object.values(userSeriesMap || {}).forEach((series) => {
        const p = series.points?.[t.xValue];
        row[series.userId] = p?.value ?? null;
      });

      const c = collectiveSeries?.[t.xValue]?.value ?? 0;
      row.collective = c;

      return row;
    });
  }, [timeline, userSeriesMap, collectiveSeries]);

  const targetValue = rootAction?.targetValue || 0;

  // ------------------------------------------------------------
  // Step 3 - Guards (AFTER hooks)
  // ------------------------------------------------------------
  if (!collectiveActions.length) {
    return (
      <p className="text-sm text-gray-500 italic mt-2">
        No collective performance data available.
      </p>
    );
  }

  if (!events.length || !timeline.length) {
    return (
      <p className="text-sm text-gray-500 italic mt-2">
        No contribution activity recorded yet.
      </p>
    );
  }

  // ------------------------------------------------------------
  // Step 4 - Render
  // ------------------------------------------------------------
  return (
    <div className="w-full mt-4">

      {/* 👥 User filter chips (Habits-style) */}
      <div className="flex flex-wrap gap-2 mb-3">
        {Object.values(userSeriesMap || {}).map((series, idx) => {
          const active = visibleUsers.includes(series.userId);
          const color = USER_COLORS[idx % USER_COLORS.length];

          return (
            <button
              key={series.userId}
              onClick={() => toggleUser(series.userId)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                active ? "text-white" : "text-gray-400 bg-white"
              }`}
              style={{
                backgroundColor: active ? color : "transparent",
                borderColor: color,
              }}
            >
              {series.userName}
              {series.userId === loggedInUserId && (
                <span className="ml-1 text-[10px] font-bold">(Me)</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="w-full h-72">
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />

            {/* X Axis uses xValue index; label formatted using tickFormatter */}
            <XAxis
              dataKey="xValue"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(x) => {
                const found = chartData.find((d) => d.xValue === x);
                return found?.xLabel || "";
              }}
              fontSize={12}
            />

            <YAxis />
            <Tooltip
              labelFormatter={(x) => {
                const found = chartData.find((d) => d.xValue === x);
                return found?.xLabelLong || "";
              }}
            />

            {/* 🎯 Target */}
            <ReferenceLine
              y={targetValue}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              label="Target"
            />

            {/* 🟠 Collective cumulative (delta-based) */}
            <Line
              type="monotone"
              dataKey="collective"
              stroke="#f97316"
              strokeWidth={3}
              dot={false}
              connectNulls
              name="Collective"
            />

            {/* 👥 Per-user actualValue snapshot lines (forward-filled) */}
            {Object.values(userSeriesMap || {}).map((series, idx) => {
              if (!visibleUsers.includes(series.userId)) return null;

              const color = USER_COLORS[idx % USER_COLORS.length];
              const isMe = series.userId === loggedInUserId;

              return (
                <Line
                  key={series.userId}
                  type="monotone"
                  dataKey={series.userId}
                  stroke={color}
                  strokeWidth={isMe ? 2.6 : 2.0}
                  dot={{ r: 5 }}          // ✅ larger dots
                  activeDot={{ r: 7 }}    // ✅ larger active dot
                  connectNulls            // ✅ keep connected
                  name={series.userName}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EtUserActionPerformanceCollectiveTimeSeriesChart;